// Motion timing. CSS is the single source of truth: `src/styles/tokens.css`
// declares every duration, and JS reads the *computed* custom property so
// media-query overrides (touch) and the prefers-reduced-motion override apply
// automatically without a second set of numbers to keep in sync.
//
// The values below are fallbacks for non-DOM contexts (SSR, unit tests) and are
// asserted against tokens.css by motion.test.js — if they drift, CI fails.

export const MOTION_TOKEN_DEFAULTS = {
  '--dur-instant': 90,
  '--dur-fast': 140,
  '--dur-base': 200,
  '--dur-slow': 320,
  '--dur-drawer': 300,
  '--dur-page-flip': 600,
  '--dur-zoom': 500,
  '--dur-dfc-flip': 600,
  '--dur-foil-sheen': 5330,
  '--dur-pill-enter': 600,
  '--dur-pill-exit': 450,
  '--dur-pill-word-delay': 360,
  '--dur-pill-word-stagger': 180,
};

/**
 * Parse a CSS duration string into milliseconds.
 * @param {unknown} value e.g. '420ms', '0.6s'
 * @returns {number} milliseconds, or NaN if not a valid duration
 */
export function parseDurationToken(value) {
  if (typeof value !== 'string') return NaN;
  const v = value.trim();
  if (v.endsWith('ms')) return parseFloat(v);
  if (v.endsWith('s')) return parseFloat(v) * 1000;
  return NaN;
}

/**
 * Read a duration token's computed value in milliseconds.
 * @param {string} name custom property name, e.g. '--dur-page-flip'
 * @param {{ root?: Element }} [opts] element to resolve against (default :root)
 * @returns {number} milliseconds
 */
export function readDuration(name, opts = {}) {
  const fallback = MOTION_TOKEN_DEFAULTS[name] ?? 0;
  if (typeof document === 'undefined') return fallback;
  const el = opts.root ?? document.documentElement;
  const ms = parseDurationToken(getComputedStyle(el).getPropertyValue(name));
  return Number.isFinite(ms) ? ms : fallback;
}
